import { createApp } from "vue";
import App from "./App.vue";
import "./assets/styles.css";
import i18n from "./i18n";
import store from "./store";
import router from "./router";
import axios from "axios";

import AutoTranslate from "./plugins/autoTranslate";

// Tag every API request with the active locale so the Laravel SetLocale
// middleware can serve translated DB content (category names, product
// names, descriptions). The frontend reads i18n.global.locale.value on
// every request, so swapping language via setLocale() takes effect on
// the next fetch without touching any individual call site.
axios.interceptors.request.use((config) => {
  try {
    const lang = i18n.global.locale.value || "fr";
    config.headers["Accept-Language"] = lang;
  } catch {
    // i18n not initialised yet (early boot) — let the request go through;
    // the backend will fall back to its configured fallback locale.
  }
  return config;
});

// Single switchable backend origin.
//   - Production build (npm run build): VUE_APP_API_BASE is unset → falls back to https://scap.ma
//   - Local dev (npm run serve):        set VUE_APP_API_BASE in .env.development.local
//
// Existing call sites pass full URLs like `https://scap.ma/api/...`. The
// request interceptor below rewrites that prefix at runtime so we don't
// have to touch every file. New code can use plain `/api/...` instead.
const API_BASE = process.env.VUE_APP_API_BASE || "https://scap.ma";
const PROD_ORIGIN = "https://scap.ma";

axios.interceptors.request.use(
  (config) => {
    if (config.url && API_BASE !== PROD_ORIGIN && config.url.startsWith(PROD_ORIGIN)) {
      config.url = API_BASE + config.url.slice(PROD_ORIGIN.length);
    }

    const clientToken     = localStorage.getItem("clientToken");
    const vendeurToken    = localStorage.getItem("vendeurToken");
    const livreurToken    = localStorage.getItem("livreurToken");
    const adminToken      = localStorage.getItem("adminToken");
    const superAdminToken = localStorage.getItem("superAdminToken");

    // Pick the right token for the target endpoint instead of relying on
    // a fixed priority that always wins for whichever role logged in
    // first. Stale tokens from a prior role in the same browser tab would
    // otherwise be sent to /api/admin/* and trip the new admin.only
    // middleware (403). The path-aware rule below routes each prefix to
    // its matching token, with sensible fallbacks.
    const url = String(config.url || "");
    const isAdminPath   = /\/api\/admin\//.test(url) || /\/api\/admins(\b|\/)/.test(url);
    const isVendorPath  = /\/api\/vendeur(s)?\b/.test(url) || /\/api\/vendor\b/.test(url);
    const isLivreurPath = /\/api\/livreur(s)?\b/.test(url);

    let token = null;
    if (isAdminPath) {
      token = superAdminToken || adminToken;
    } else if (isVendorPath) {
      token = vendeurToken;
    } else if (isLivreurPath) {
      token = livreurToken;
    }
    if (!token) {
      // Fallback for unknown routes: same precedence as before so
      // existing flows that depended on the cascade keep working.
      token = clientToken || vendeurToken || livreurToken || adminToken || superAdminToken;
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const app = createApp(App);

app.use(router).use(i18n).use(store);

// If your UI source text is French (it is), keep pageLanguage:"fr"
app.use(AutoTranslate, {
  pageLanguage: "fr",
  includedLanguages: ["fr", "en", "ar"],
  debounceMs: 350,
});

app.mount("#app");
